/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @typescript-eslint/naming-convention */
import { HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { LocalStorageService } from '../localstorage.service';
import * as dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import * as advancedFormat from 'dayjs/plugin/advancedFormat';
import * as isToday from 'dayjs/plugin/isToday';
import * as isYesterday from 'dayjs/plugin/isYesterday';
import * as utc from 'dayjs/plugin/utc';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import { DataCollectorService } from '../data_collector_services/data_collector_service.service';
import { AttachmentType } from '../../../components/notes/notes.interface';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { AbstractControl, UntypedFormArray, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { redirectUrl } from '../../../components/datagrid/datagrid.interface';
import _, { isEmpty } from 'lodash';
import { BadgeOptions } from 'app/components/badge/badge.interface';
import moment, { Moment, min } from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ZuperDialogService } from '@zuper/components/zuper-dialog';
import { NgEventBus } from 'ng-event-bus';
import { debounceTime, map, pairwise } from 'rxjs/operators';
import RRule from 'rrule';
import { LocationStrategy } from '@angular/common';
import { ProductSubscriptionAlertComponent } from 'app/components/product-subscription-alert/product-subscription-alert.component';
import { environment } from 'environments/environment';
import { LANG_COUNTRY } from 'app/core/constants/constants';
import { Clipboard } from '@angular/cdk/clipboard';
import { HotToastService } from '@ngneat/hot-toast';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  localizationValue: any;
  filterLoaded: Subject<boolean> = new Subject<boolean>();
  rescheduleConfirmation: Subject<boolean> = new Subject<boolean>();
  updateNewTimeoffConfirmation: Subject<boolean> = new Subject<boolean>();
  companyLogo: Subject<String> = new Subject<String>();
  apiUrl: any = {
    'upload': `${this._localStorageService.baseUrl}/misc/upload`
  };
  errorMessage: any = {
    required: 'text.field_is_required',
    max: 'field.max.error.message',
    min: 'field.min.error.message',
    pattern: 'field.pattern.error.message',
    email: 'field.email.error.message'
  };
  public OnZuperDateSelected: Subject<any> = new Subject<any>();
  public OnZuperDateUpdated: Subject<any> = new Subject<any>();
  public onUpdateMenuCards: Subject<any> = new Subject<any>();
  basicSupportedFileTypes: string[] = ['image', 'video', 'audio', 'pdf', 'doc', 'xls'];
  basicWithCompressSupportedFileTypes: string[] = [...this.basicSupportedFileTypes, 'compress'];
  notesSupportedFileTypes: string[] = [...this.basicWithCompressSupportedFileTypes, 'application'];
  // eslint-disable-next-line max-len
  // Extensions must be maintained in lowercase
  supportedExtensions: string[] = ['png', 'jpg', 'jpeg', 'gif', 'mp3', 'wav', 'm4a', 'wma','mp4','MP4','mkv', 'avi', 'flv', 'mov', 'mpg', '3gp', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'pdf', 'webp', 'webm', 'msg', 'eml', 'dng', 'zip'];
  acceptedFileFormat: string = 'image/*,audio/*,video/*,application/pdf,application/vnd.ms-excel,application/msword,.doc,.docx,.xls,.xlsx,.zip,.msg,.eml';
  settingsActiveConfig = signal({
    settingsActive: false,
    newSettingsActive: false
  });
  allowedFileFormats = {
    [AttachmentType.IMAGE]: `.png',.PNG,.jpg,.JPG,.jpeg,.JPEG,.gif,.webp,.dng,.DNG`,
    [AttachmentType.AUDIO]: `.mp3,.wav,.m4a,.wma,.MP3,.WAV,.M4A`,
    [AttachmentType.VIDEO]: `mp4,.avi,.flv,.mov,.mpg,.3gp,.webm,.mkv,'.MP4'`,
    [AttachmentType.DOCUMENT]: `doc,.docx,.xls,.xlsx,.txt,.pdf,.DOCX,.DOC`,
  }

  constructor(private _localStorageService: LocalStorageService, private dataCollectorService: DataCollectorService, private translateService: TranslateService, private zuperDialogService: ZuperDialogService, private _eventBus: NgEventBus, private locationStrategy: LocationStrategy
    ,private clipboard: Clipboard, private toast: HotToastService
  ) { 
    this.loadLocale();
    const settingsActiveConfig: any = this._localStorageService.getLocalStorage('settings_active_config');
    
    if (settingsActiveConfig) {
      this.settingsActiveConfig.set(settingsActiveConfig);
    } 
    else {
      this.settingsActiveConfig.set({
        settingsActive: false,
        newSettingsActive: false
      });
    }
  }

  // flatterns the obj with the first level nested obj
  flatternNestedObjFun(data): any {
    if (data.length) {
      return data.map((record: any) => {
        Object.keys(record).forEach((obj: any) => {
          if (typeof (record[obj]) === 'object' && record[obj] != null && !Array.isArray(record[obj])) {
            // eslint-disable-next-line guard-for-in
            for (const key in record[obj]) {
              // Store temp in result
              record[obj + '.' + key] = record[obj][key];
              if (typeof (record[obj][key]) === 'object' && record[obj][key] != null && !Array.isArray(record[obj][key])) {
                // eslint-disable-next-line guard-for-in
                for (const nestedKey in record[obj][key]) {
                  if (nestedKey) {
                    record[obj + '.' + key + '.' + nestedKey] = record[obj][key][nestedKey];
                  }
                }
              }
            }
          }
          if (obj === 'custom_fields' && Array.isArray(record[obj])) {
            for (const key of record[obj]) {
              // Store temp in result
              record[`${obj}.${key?.group_name ? key?.group_name : 'default'}.${key?.label}`] = key?.value;
            }
          }
        });
        return record;
      });
    } else {
      return [];
    }
  }

  public get timeFormat(): any {
    const userTimeFormat = this._localStorageService.getLocalStorage('user_preferences')?.time_format || '12_HRS';
    const timeFormat = {
      '12_HRS': 'hh:mm A',
      '24_HRS': 'HH:mm'
    };
    return timeFormat[userTimeFormat];
  }

  public get showMeridian(): boolean {
    const userTimeFormat = this._localStorageService.getLocalStorage('user_preferences')?.time_format || '12_HRS';
    if (userTimeFormat === '12_HRS') {
      return true;
    } else {
      return false;
    }
  }

  public get dateTimeFormat(): any {
    const date = this._localStorageService.getLocalStorage('user_preferences')?.date_format || 'MM/DD/YYYY';
    const format = `${date} ${this.timeFormat}`;
    this._localStorageService.setLocalStorage('user_date_time_format',format);
    return format;
  }

  public get dateFormat(): any {
    return this._localStorageService.getLocalStorage('user_preferences')?.date_format || 'MM/DD/YYYY';
  }

  public get dateTimeFormatWithTz(): any {
    const date = this._localStorageService.getLocalStorage('user_preferences')?.date_format || 'MM/DD/YYYY';
    return `${date} ${this.timeFormat} (z)`;
  }

  public get dateFormatWithTz(): any {
    return `${this._localStorageService.getLocalStorage('user_preferences')?.date_format || 'MM/DD/YYYY'} z`;
  }

  public get timeZone(): any {
    return this._localStorageService.getLocalStorage('user_preferences')?.timezone || dayjs?.tz?.guess();
  }

  // created a new method for timeZonePicker globally not to disturb old timeZone method(above)
  public get preferredTimeZone(): string {
    return this._localStorageService.getLocalStorage('user_preferences')?.timezone ||
      this._localStorageService.getLocalStorage('company')?.company_timezone ||
      dayjs?.tz?.guess();
  }

  get isTimeZoneEnabled(): boolean {
    return (
      this._localStorageService.getLocalStorage('company_configuration_data')
        ?.general?.allow_switching_timezones || false
    );
  }

  public get companyCountry(): string {
    const countryCode: string = this._localStorageService.getLocalStorage('company')?.company_country;
    return countryCode ? countryCode.toLowerCase() : 'us';
  }

  public get companyCurrency(): string {
    const companyCurrency: string = this._localStorageService.getLocalStorage('company')?.company_currency;
    return companyCurrency ? companyCurrency : 'USD';
  }

  public get user(): any {
    const user: any = this._localStorageService.getLocalStorage('user');
    return user ? user : null;
  }

  public get companyPolicy(): any {
    const companyPolicy: any = this._localStorageService.getLocalStorage('company_policy');
    return companyPolicy ? companyPolicy : null;
  }

  public get companyConfig(): any {
    return this._localStorageService.getLocalStorage('company_configuration_data') ?? null;
  }

  public constructParam(params): any {
    let paramString = new HttpParams();
    for (const key of Object.keys(params)) {
      paramString = paramString.set(key, params[key]);
    }
    return paramString;
  }

  public get numberMinValueValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {

      const value = control.value;

      if (this.isEmptyOrUndefined(value)) {
        return null;
      }

      return value > 0 ? null : { validMinValue: true };
    };
  }

  // Convert Date and time  without UTC
  public formatDateTimeToStandard(value, format, field): any {
    if (value) {
      value = dayjs(value, this.getFormatBasedonType(field))?.format(format);
    }
    return value;
  }

  public formatTimeFromDefaultFormat(value, format): any {
    if (value) {
      value = dayjs(value, format)?.format();
    }
    return value;
  }

  // Convert Date and time  without UTC
  public formatDateTimeToUserDefined(value, format, field): any {
    if (value) {
      value = this.isEmptyOrUndefined(format) ?
        dayjs(value, this.getFormatBasedonType(field))?.format(this.getFormatBasedonType(field)) :
        dayjs(value, format)?.format(this.getFormatBasedonType(field));
    }
    return value;
  }

  //convert date/datetime/time to utc format with the given format
  public convertDate(value, format, field): any {
    if (value) {
      value = dayjs(value, this.getFormatBasedonType(field))?.utc()?.format(format);
    }
    return value;
  }

  public convertDateToCustomGroupFormat(value, format): any {
    if (value) {

      if(dayjs(value).isToday()) {
        return 'Today';
      }

      if(dayjs(value).isYesterday()) {
        return 'Yesterday';
      }

      value = dayjs(value)?.format(format);
    }
    return value;
  }

  //convert to specific type to passed data
  public convertDateToSpecifiedFormat(value, format, field = 'date'): any {
    if (value) {
      value = dayjs(value, this.getFormatBasedonType(field))?.format(format);
    }
    return value;
  }

  public convertBackFromUTC(value, format, field, timeZone?: string): any {
    if (value) {
      value = dayjs(dayjs?.utc(value, format))?.tz(timeZone ? timeZone : this.timeZone)?.format(this.getFormatBasedonType(field));
    }
    return value;
  }

  public convertBackToTimeWithoutTz(value, format, field): any {
    if (value) {
      value = dayjs(dayjs(value, format))?.format(this.getFormatBasedonType(field));
    }
    return value;
  }

  getFormatBasedonType(type: string): string {
    switch (type.toLowerCase()) {
      case 'date':
        return this.dateFormat;
      case 'datetime':
        return this.dateTimeFormat;
      case 'time':
        return this.timeFormat;
    }
  }

  public convertBackToUTC(value, format): any {
    if (value) {
      value = dayjs(dayjs.utc(value, format)).tz(this.timeZone)?.format();
    }
    return value;
  }

  public convertTimeToUTC(value: any, format = 'HH:mm:ss'): any {
    if (value) {
      value = dayjs(value, this.timeFormat)?.utc(true)?.tz(this.timeZone)?.format(format);
    }
    return value;
  }

  public convertDateWithMonthNames(value: any, format: string): string {
    if (value) {
      value = dayjs(value)?.format(format);
    }
    return value;
  }

  public convertDateWithUTC(value: any, format: string, isKeepLocalTime: boolean = true): string {
    if (value) {
      return dayjs(value).utc(isKeepLocalTime).format(format);
    }
    return value;
  }

  get stepValue(): number {
    const defaultDecimalLimit = this._localStorageService.getLocalStorage('company_configuration_data')?.general?.decimal_roundoff || 2;
    return 1 / 10 ** defaultDecimalLimit;
  }
  public convertModuleName(value: any): string {
    const data = {
      'ESTIMATES': 'Quotes',
      'SERVICE_CONTRACTS': 'Contracts',
      'USER': 'Employees'
    };
    if (data?.[value?.module_key]) {
      return data[value.module_key];
    } else {
      return value.module_name;
    }
  }

  isEmptyOrUndefined(value: any): boolean {
    if ((typeof value) === 'number' || (typeof value) === 'boolean') {
      return false;
    }
    return ['', null, undefined, '[]'].includes(value) || isEmpty(value);
  }

  isValidObjectAllProperties(obj: any): boolean {
    return Object.values(obj).every((prop: any) => {
      if (prop === null || prop === undefined || prop === '') {
        return true;
      }
      return false;
    });
  }

  getLink(module: string, uid: string,user?: any): string {
    return module === 'SERVICE_CONTRACTS' ? this.allowUserToViewContract(user) ? `/${redirectUrl[module]}/${uid}/details` : '' : `/${redirectUrl[module]}/${uid}/details`;
  }

  public getFileSize(size: number): string {
    const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (size === 0) {
      return '0 Byte';
    }
    const index: number = parseInt(Math.floor(Math.log(size) / Math.log(1024)).toString(), 10);
    return Math.round(size / Math.pow(1024, index)) + ' ' + sizes[index];
  }

  public getFilePreviewURL(link: string): string {
    const docs: string[] = ['doc', 'docx', 'xls', 'xlsx', 'txt', 'pdf', 'DOCX', 'DOC'];
    if (docs.includes(this.getAttachmentExtension(link))) {
      return 'https://docs.google.com/gview?url=' + link;
    }
    return link;
  }

  public getMapsCoordinatesUrl(latitude: any, longitude: any): string {
    return `https://maps.google.com/?q=${latitude},${longitude}`;
  }

  /**
   * Retrieves the localized value for a given translation key.
   * Falls back to displayName, then to the key itself if not found.
   * @param key The translation key to look up.
   * @param displayName Optional fallback display name.
   * @returns The localized string, displayName, or key.
   */
  public getLocaleValue(key: string, displayName?: any): any {
    // If no key is provided, return displayName or empty string
    if (!key) {
      return displayName || '';
    }
    // Try to get the translation using ngx-translate's instant method
    const instant: string = this.translateService?.instant(key);
    // If a translation is found and it's not the same as the key, return it
    if (instant && instant !== key) {
      return instant;
    }
    // Fallback: check localizationValue object, then displayName, then key
    return this.localizationValue?.[key] ?? displayName ?? key;
  }

  public loadLocale(): any {
    let lang: string = JSON.parse(localStorage.getItem('user_preferences'))?.language || 'en';
    lang = lang.toLowerCase();
    const isB2BConfigEnabled = this.isCompanyB2BEnabled();

    // Determine selected language (with '-b2b' suffix if applicable)
    const selectedLanguage = isB2BConfigEnabled ? `${lang}-b2b` : lang;

    // Build the file path for the language JSON
    const filePath = `${this.locationStrategy.getBaseHref()}assets/i18n/${lang}/${selectedLanguage}.json`;

    // Fetch localization data
    this.dataCollectorService.getData(filePath).subscribe((response: any) => {
      this.localizationValue = { ...this.localizationValue, ...response };
    });
  }

  public getAssetSerialNumber(assetSerialNumber: any): string {
    if (assetSerialNumber?.length && Array.isArray(assetSerialNumber)) {
      return assetSerialNumber[0];
    }
    return assetSerialNumber;
  }

  public getAttachmentExtension(link: string): string {
    let type: string = '';
    if (link) {
      const splittedValue: string[] = link?.split('.');
      type = splittedValue[splittedValue.length - 1];
    }
    return type;
  }

  public getAttachmentTypeEnum(attachmentType: string): string {
    const images: string[] = ['png', 'PNG', 'jpg', 'JPG', 'jpeg', 'JPEG', 'gif', 'webp', 'dng', 'DNG', 'svg', 'SVG'];
    const audio: string[] = ['mp3', 'wav', 'm4a', 'wma', 'MP3', 'WAV', 'M4A'];
    const video: string[] = ['mp4', 'avi', 'flv', 'mov', 'mpg', '3gp', 'webm', 'mkv'];
    const docs: string[] = ['doc', 'docx', 'xls', 'xlsx', 'txt', 'pdf', 'DOCX', 'DOC'];
    if (images.includes(attachmentType)) {
      return AttachmentType.IMAGE;
    } else if (audio.includes(attachmentType)) {
      return AttachmentType.AUDIO;
    } else if (video.includes(attachmentType)) {
      return AttachmentType.VIDEO;
    } else if (docs.includes(attachmentType)) {
      return AttachmentType.DOCUMENT;
    }
    return AttachmentType.DOCUMENT;
  }

  public getCurrencyDecimal(): number {
    return this._localStorageService.getLocalStorage('company_configuration_data')?.general?.decimal_roundoff || 2;
  }

  ConfirmPasswordValidator(controlName: string, matchingControlName: string): any {
    return (formGroup: UntypedFormGroup): any => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];
      if (
        matchingControl.errors &&
        !matchingControl.errors.confirmPasswordValidator
      ) {
        return;
      }
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ confirmPasswordValidator: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  public isURL(value: string): boolean {
    if (value) {
      const regExp: string = '^(https?://)?(www\\.)?([-a-z0-9]{1,63}\\.)*?[a-z0-9][-a-z0-9]{0,61}[a-z0-9]\\.[a-z]{2,6}(/[-\\w@\\+\\.~#\\?&/=%]*)?$';
      const url: RegExp = new RegExp(regExp, 'i');
      return url.test(value);
    }
    return false;
  }

  public isImageFileLink(value: string): boolean {
    return (value.match(/\.(jpeg|jpg|gif|png|svg)$/) !== null);
  }

  public getFormattedAddress(address: any,includeCountry = true): string {
    const addressArray: string[] = [];
    if(address){
      if (address.street) {
        addressArray.push(this.getUniqueStreetValue(address));
      }
      if (address.landmark) {
        addressArray.push(address.landmark);
      }
      if (address.city) {
        addressArray.push(address.city);
      }
      if (address.state) {
        addressArray.push(address.state);
      }
      if (address.country && includeCountry) {
        addressArray.push(address.country);
      }
      if (address.zip_code) {
        addressArray.push(address.zip_code);
      }
    }
    return addressArray.length > 0 ? addressArray.join(', ') : '';
  }

  public hasModuleAccess(key: string, hide_module?: boolean, from_sidebar?: string): boolean {
    let permittedModules = [];
    const permissions = _.map(this._localStorageService.getLocalStorage('user_permissions'), 'permissions');
    if (permissions) {
      permittedModules = _.map(permissions, 'module');
      permittedModules = _.uniq(_.map(permittedModules, 'module_key'));
    }
    const user = this._localStorageService.getLocalStorage('user');
    const companyModules = _.map(this._localStorageService.getLocalStorage('company_modules'), 'module');
    if (companyModules.length === 0) {
      if (hide_module && hide_module === true) {
        return false;
      }
      else {
        return true;
      }
      // return false
    }
    else {
      if (_.find(companyModules, m => m.module_key === key)) {
        if (user.access_role && !_.isEmpty(user.access_role) && from_sidebar === 'from_sidebar' && permittedModules && permittedModules.length > 0) {
          if (_.includes(permittedModules, key)) {
            return true;
          }
          else {
            return false;
          }
        }
        else {
          return true;
        }
      }
      else {
        return false;
      }
    }
  }

  public hasPermission(key: string): boolean {

    // Check if the user has an access role
    const user = this._localStorageService.getLocalStorage('user');

    if (!user) {
      return true;
    }

    // If the user has no access role, allow them to access the feature

    if (!user.access_role || _.isEmpty(user.access_role)) {
      return true;
    }

    const permissions = _.map(this._localStorageService.getLocalStorage('user_permissions'), 'permissions');

    if (user.access_role && permissions?.length === 0) {
      return false;
    }

    // If permissions is available, check if user has access
    const is_found = _.filter(permissions, (p: any) => String(p?.permission_key) === String(key));

    if (is_found && is_found.length > 0) {
      return true;
    }

    return false;

  }

  public getProductService(data: any): any {
    if (data['product']?.['product_ref_id']) {
      return data['product']['product_ref_id'];
    } else if (data['product']?.['product']) {
      return data['product']['product'];
    }
    return null;
  }

  hasValidProfilePicture(profilePicture: string): boolean {
    if (profilePicture) {
      return profilePicture.indexOf('profile_picture.jpg') === -1;
    }
    return false;
  }

  getActiveStatusOptions(status, module): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: this.getActiveStatusBadgeOption('type', status, module),
      displayKey: this.getActiveStatusBadgeOption('displayKey', status, module),
      displayName: this.getActiveStatusBadgeOption('displayName', status, module),
      backgroundColor: status ? '#dcfce7' : '#fee2e2',
      color: status ? '#166534' : '#991b1b',
      extraClass: 'capitalize'
    };
    return badgeOption;
  }

  uploadFile(file: File, params?: any): Observable<(any)> {
    const fd = new FormData();
    fd.append('attachment', file);
    fd.append('attachment_type', params?.attachmentType || 'JOB_NOTE');
    if(params?.module){
      fd.append('module', params.module);
    }
    if(params?.moduleUid){
      fd.append('module_uid', params.moduleUid);
    }
    return this.dataCollectorService.uploadfile(`${this.dataCollectorService.baseurl()}/misc/upload`, fd);
  }

  // getErrorMessage(formGroup: FormGroup,formField: string): string{
  //   if(formGroup?.controls[formField]?.touched){
  //     if(formGroup?.controls[formField]?.hasError('required')){
  //       return errorMessage['required'];
  //     }else if(formGroup?.controls[formField]?.hasError('max')){
  //       return errorMessage['max'];
  //     }else if(formGroup?.controls[formField]?.hasError('min')){
  //       return errorMessage['min'];
  //     }else if(formGroup?.controls[formField]?.hasError('pattern')){
  //       return errorMessage['pattern'];
  //     }else if(formGroup?.controls[formField]?.errors !== null){
  //       return formGroup?.controls[formField]?.errors?.message;
  //     }
  //   }
  // }

  getActiveStatusBadgeOption(key, value, module): string {
    switch (key) {
      case 'type':
        if (module === 'PRODUCTS') {
          return value ? 'AVAILABLE' : 'UNAVAILABLE';
        } else {
          return value ? 'ACTIVE' : 'INACTIVE';
        }
      case 'displayKey':
        if (module === 'PRODUCTS') {
          return value ? 'product.available.sufix.message' : 'text.unavailable';
        } else {
          return value ? 'active.text' : 'text.inactive';
        }
      case 'displayName':
        if (module === 'PRODUCTS') {
          return value ? 'Available' : 'Unavailable';
        } else {
          return value ? 'Active' : 'In-Active';
        }
    }
  }

  getBarcodeURL(code): string {
    return this.dataCollectorService.baseurl() +'/misc/tools/qrcode?size=360&data=' + encodeURIComponent(code);
  }

  public isTextTruncated(id: string): boolean {
    const element = document.getElementById(id);
    if (element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth) {
      return true;
    } else {
      return false;
    }
  }

  public getUniqueStreetValue(address: any): string {
    const street: string = address?.street;
    const splittedValue: string[] = street?.split(',');
    if (splittedValue?.length > 1) {
      splittedValue.forEach((value: string, index: number) => {
        if (index > 0) {
          value = value?.toString().trim();
          if (address?.landmark && value.includes(address?.landmark.toString().trim())) {
            splittedValue[index] = '';
          }
          if (address?.city && value.includes(address?.city.toString().trim())) {
            splittedValue[index] = '';
          }
          if (address?.state && value.includes(address?.state.toString().trim())) {
            splittedValue[index] = '';
          }
          if (address?.country && value.includes(address?.country.toString().trim())) {
            splittedValue[index] = '';
          }
          if (address?.zip_code && value.includes(address?.zip_code.toString().trim())) {
            splittedValue[index] = '';
          }
        }
      });
      return splittedValue.filter(value => value).join(',');
    }
    return street;
  }


  getErrorMessage(formGroup: UntypedFormGroup, formField: string, field?: any): string {
    if (formGroup?.controls[formField]?.touched) {
      if (formGroup?.controls[formField]?.hasError('required') || formGroup?.controls[formField]?.hasError('whitespace')) {
        return this.getLocaleValue(this.errorMessage['required']);
      } else if (formGroup?.controls[formField]?.hasError('max')) {
        if (field?.original_template?.max_value && field?.original_template?.min_value) {
          return this.getLocaleValue('field.number.between.error.message') + ' ' + field?.original_template?.min_value + ' ' + this.getLocaleValue('field.number.and.error.message') + ' ' + field?.original_template?.max_value;
        } else {
          return this.getLocaleValue(this.errorMessage['max']) + ' (' + field?.original_template?.max_value + ')';
        }
      } else if (formGroup?.controls[formField]?.hasError('min')) {
        if (field?.original_template?.max_value && field?.original_template?.min_value) {
          return this.getLocaleValue('field.number.between.error.message') + ' ' + (field?.original_template?.min_value ?? 0) + ' ' + this.getLocaleValue('field.number.and.error.message') + ' ' + field?.original_template?.max_value;
        } else {
          return this.getLocaleValue(this.errorMessage['min']) + ' (' + (field?.original_template?.min_value ? field?.original_template?.min_value : (formGroup?.controls[formField]?.errors?.min?.min ? formGroup?.controls[formField]?.errors?.min?.min : 0)) + ')';
        }
      } else if (formGroup?.controls[formField]?.hasError('pattern')) {
        if (field?.field_validation === 'number') {
          return this.getLocaleValue('field.pattern.number.error.message');
        }
        return this.getLocaleValue(this.errorMessage['pattern']);
      } else if (formGroup?.controls[formField]?.hasError('email')) {
        return this.getLocaleValue(this.errorMessage['email']);
      }
      else if (formGroup?.controls[formField]?.errors !== null) {
        return formGroup?.controls[formField]?.errors?.message;
      }
    }
  }


  invalidScroll(element: HTMLDivElement): void {
    const scrollElement = element.querySelector('form .ng-invalid');
    scrollElement?.scrollIntoView();
  }

  getDistance(distance: number): string {
    const companyData: any = this.companyConfig;
    const decimal: number = companyData?.general?.decimal_roundoff || 1;
    if (companyData?.general?.distance_metric === 'MILES' && (distance * 0.000621)) {
      return (distance * 0.000621).toFixed(decimal);
    }else if((distance / 1000)){
      return (distance / 1000).toFixed(decimal);
    }else{
      return '';
    }
  }

  getIsoWeek(date): any{
    const JanuaryFourth = 4;
    const numberofDaysPerWeek = 7;
    const millisecondsPerDay = 86400000;

    const dateObj = dayjs(date);
    const year = dateObj.format('YYYY');
    const millisecondsSinceEpoch = dateObj.valueOf();
    const cloneDate: any = new Date(millisecondsSinceEpoch);
    const dayNumberBasedOnISO = (cloneDate.getDay() + 6) % numberofDaysPerWeek;
    const thursdayOfISOweek = cloneDate.getDate() - dayNumberBasedOnISO + 3;
    cloneDate.setDate(thursdayOfISOweek);
    const firstThursdayOfYear: any = new Date(cloneDate.getFullYear(), 0, JanuaryFourth);
    const weekNumber = Math.round(((cloneDate - firstThursdayOfYear) / millisecondsPerDay + firstThursdayOfYear.getDay() + 1) / numberofDaysPerWeek);
    return `W${weekNumber} ${year}`;
  }

  getCurrentQuarterOfYear(date): string{
    const dateObj = dayjs(date);
    const month = dateObj.month();
    const quarter = Math.floor(month / 3) + 1;
    const year = dateObj.year();
    return `Q${quarter} ${year}`;
  }

  getNextQuarterHour(dateTime: any,toTopOfHour: boolean = false,beforeQuarterHour: boolean = false): Moment {
    const currentTime = moment(dateTime);
    if(currentTime.isValid()) {
      const minutes = currentTime.minutes();
      const hour = currentTime.hour();
      if (beforeQuarterHour) {
        // Calculate the previous quarter hour
        const previousQuarterHour = Math.floor((minutes - 1) / 15) * 15;
        if (toTopOfHour && minutes % 15 !== 0) {
          return currentTime.clone().hour(hour).minutes(0).seconds(0);
        } else if (minutes % 15 !== 0) {
          return currentTime.clone().minutes(previousQuarterHour).seconds(0);
        }
      } else {
        // Calculate the next quarter hour
        const nextQuarterHour = Math.ceil((minutes + 1) / 15) * 15;
        if (toTopOfHour && minutes % 15 !== 0) {
          return currentTime.clone().hour(hour + 1).minutes(0).seconds(0);
        } else if (minutes % 15 !== 0) {
          return currentTime.clone().minutes(nextQuarterHour).seconds(0);
        }
      }
    }
    return dateTime;
  }

  getNextQuarterStepperMin(dateTime: Moment): number {
    const minutes = moment(dateTime).clone().minutes();
    const nextQuarter = Math.ceil(minutes / 15) * 15;
    return nextQuarter === minutes ? 15 : nextQuarter - minutes;;
  }

  hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex?.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  getLuminance({ r, g, b }: { r: number; g: number; b: number }): number {
    const a = [r, g, b].map((v: any) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
  }

  isLightColor(luminance: number): boolean {
    return luminance > 0.179;
  }

  getTextColorBasedOnBg(bgColor: string): string {
    const rgb = this.hexToRgb(bgColor);
    if (!rgb) {
      return '#000000';
    };
    const luminance = this.getLuminance(rgb);
    return this.isLightColor(luminance) ? '#000000' : '#FFFFFF';
  }

  getHours(time: number): string {
    if (time) {
      return moment.unix(time).utc().format('H');
    }
    return '';
  }

  getMin(time: number): string {
    if (time) {
      return moment.unix(time).utc().format('m');
    }
    return '';
  }

  getTimeFromMin(time): string {
    if (time) {
      const hours = (time / 60).toString().split('.')[0];
      const min = (time % 60).toString();
      return `${Number(hours) >= 1 ? hours + 'h' : '0h'} ${Number(min) >= 1 ? min + 'm' : '0m'}`;
    }
    else {
      return '0h 0m';
    }
  }

  getTimeLogValue(totalTime: number): { hours: number; minutes: number } {
    const logValues: { hours: number; minutes: number } = {
      hours: 0,
      minutes: 0
    };
    if (totalTime) {
      logValues.minutes = totalTime % 60;
      logValues.hours = Math.trunc(totalTime / 60);
    }
    return logValues;
  }

  convertMSecToHrsAndMin(milliseconds: number): string {
    const duration = moment.duration(milliseconds);
    const hours = Math.floor(duration.asHours());
    const minutes = Math.floor(duration.asMinutes()) % 60;
    if (hours === 0 && minutes === 0) {
      return '0 min';
    }
    return hours < 1 ? `${minutes} ${this.translateService.instant('route.min')}` :
      minutes < 1 ? `${hours} ${this.translateService.instant('route.hours')} ` :
        `${hours} ${this.translateService.instant('route.hours')} ${minutes} ${this.translateService.instant('route.min')}`;
  }


  convertSecondsToHours(seconds: number, unitShortForm: boolean = false): string {
    const totalMinutes = seconds / 60;
    const localeHour = unitShortForm ? 'hr': this.getLocaleValue('label.hour', 'hour');
    const localeHours = unitShortForm ? 'hrs': this.getLocaleValue('label.hours', 'hours');
    const localeMinute = unitShortForm ? 'min': this.getLocaleValue('label.minute', 'minute');
    const localeMinutes = unitShortForm ? 'mins': this.getLocaleValue('label.minutes', 'minutes');
    if (!totalMinutes) { return `${0} ${localeMinute}`; }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const h = hours > 0 ? `${hours?.toFixed()} ${Number(hours?.toFixed()) <= 1 ? localeHour : localeHours}` : '';
    const m = minutes > 0 ? ` ${minutes?.toFixed()} ${Number(minutes?.toFixed()) <= 1 ? localeMinute : localeMinutes}` : '';
    return `${h} ${m}`.trim();
  }
  getTimeLogValueWithDays(totalTime: number): { days: number; hours: number; minutes: number } {
    const logValues: { days: number; hours: number; minutes: number } = {
      days: 0,
      hours: 0,
      minutes: 0
    };
    if (totalTime) {
      logValues.days = Math.trunc(totalTime / 1440); // 1440 minutes in a day
      const remainingMinutesAfterDays = totalTime % 1440;
      logValues.hours = Math.trunc(remainingMinutesAfterDays / 60);
      logValues.minutes = remainingMinutesAfterDays % 60;
    }
    return logValues;
  }

  convertMinstoHours(data): any {
    let total_mins;
    if (data) {
      total_mins = data;
      const hrs = Math.floor(total_mins / 60);
      const mins = Math.floor(total_mins - (60 * hrs));
      return hrs + 'h ' + mins + 'm';
    }
  }
  getErrorHandlerToastMessage(error, displayKey = 'text.unable_to_complete_the_requested_action', displayName = 'Unable to complete the requested action'): any {
    return (error?.data?.message ?? error?.error?.message ?? error?.message ?? this.getLocaleValue(displayKey, displayName));
  }

  convertHexToRgba(colorCode: string, opacity?: string): string {
      if (colorCode) {
          const result: RegExpExecArray = (colorCode.length > 4 ? /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i : /^#?([a-f\d]{1})([a-f\d]{1})([a-f\d]{1})$/i)
              .exec(colorCode);
          if (result) {
            if(!opacity) {
              opacity = '0.15';
            }
            return 'rgba(' + parseInt(result[1], 16) + ',' + parseInt(result[2], 16) + ',' + parseInt(result[3], 16) + ', ' + opacity + ')';
          }
      }
      return null;
  }

  darkenHex(hex: string, percent: number): string {
    percent = Math.max(0, Math.min(100, percent));
    hex = hex.replace(/^#/, '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
    const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
    const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`.toUpperCase();
}

  isFinanceable(quoteAmount): any {
    let isFinanceable = false;
    if(this.companyPolicy?.financing_enabled === true && this.companyConfig?.financing){
      const { is_enabled , max_amount, min_amount } = this.companyConfig?.financing;
      if (is_enabled && min_amount < quoteAmount && quoteAmount < max_amount) {
        isFinanceable = true;
      }
    }
    return isFinanceable;
  }

  getUpdatedLineItemsArray(lineItem: any[]): any{
    let indexValue = 1;
    lineItem.forEach((item)=>{
      if(item?.line_item_type !== 'HEADER'){
        item.lineItemId = indexValue;
        indexValue +=1;
      }else{
        item.lineItemId = null;
      }
    });
    return lineItem;
  }

  getTimezoneDisplayName(timezoneKey: string): string {
    let timezoneDisplayName: string = '';
    if(timezoneKey) {
      timezoneDisplayName = `(GMT${moment().tz(timezoneKey).format('Z')}) ${dayjs().tz(timezoneKey).format('zzz')}`;
    }
    return timezoneDisplayName;
  }

  isValidGeoCords(geoCords: any[]): boolean {
    //Consider [0, 0] as invalid and [n, 0], [0, n] is valid geo-cords
    return geoCords?.length === 2 &&
      typeof geoCords[0] === 'number' && typeof geoCords[1] === 'number' &&
      !isNaN(geoCords[0]) && !isNaN(geoCords[1]) &&
      !(geoCords[0] === 0 && geoCords[1] === 0);
  }

  isValidTimezone(timeZone: string): boolean {
    if (!timeZone) { return false; }
    try {
      dayjs.tz('2024-01-01', timeZone);
      return true;
    } catch (e) {
      return false;
    }
  }

  isPlainPositiveNumber(input: string): boolean {
    const plainNumberRegex = /^[0-9]+(\.[0-9]+)?$/;
    return plainNumberRegex.test(input);
}

  convertISOToTimezone(isoString: string,  timeZone: string, unit: 'startOf' | 'endOf',momentUnit: moment.unitOfTime.Base): string {
    const utcMoment = moment.utc(isoString);
    const localMoment = utcMoment.tz(timeZone);
    const finalMoment = unit === 'endOf' ? localMoment.endOf(momentUnit) : localMoment.startOf(momentUnit);
    return finalMoment.format('YYYYMMDDTHHmmss') + 'Z';
  }


  updateRruleWithTimezone(rrule: string, timeZone: string): string {
    if (timeZone) {
      const rule = RRule.fromString(rrule);
      const utcStartDate = moment.utc(rule.options.dtstart);
      const utcUntilDate = moment.utc(rule.options.until);
      const adjustedStartDateUTC = utcStartDate.tz(timeZone).format('YYYYMMDDTHHmm00[Z]');
      const adjustedUntilDateUTC = utcUntilDate.tz(timeZone).format('YYYYMMDDTHHmm00[Z]');
      const newRRuleString = rrule
      .replace(/DTSTART=\d{8}T\d{6}(Z)?/, `DTSTART=${adjustedStartDateUTC}`)
      .replace(/UNTIL=\d{8}T\d{6}(Z)?/, `UNTIL=${adjustedUntilDateUTC}`);
      return newRRuleString;
    }
    return rrule;
  }

  getRruleText(rrule: string, timeZone?: string): string {
    let rruleText: string = '';
    if (rrule) {
      try {
        if (timeZone) {
          const newRRuleString = this.updateRruleWithTimezone(rrule, timeZone);
          const newRule = RRule.fromString(newRRuleString);
          rruleText = newRule.toText();
        }
        else {
          rruleText = RRule?.fromString(rrule).toText();
        }
      }
      catch {
        rruleText = '';
      }
    }
    return rruleText;
  }

  concatTwoStrings(value1: string, value2: string, delimiter: string = ' '): string {
    let result = '';
    if (value1 && value2) {
      result = value1 + delimiter + value2;
    }
    else if (value1) {
      result = value1;
    }
    else if (value2) {
      result = value2;
    }
    return result;
  }

  sendUpdate(data: any): any {
    this.rescheduleConfirmation.next(data);
  }

  getUpdates(): any {
    return this.rescheduleConfirmation.asObservable();
  }

  compareKeysInArrayOfObjects(array1, array2, keys): boolean {
    return _.isEqual(
      _.map(array1, (obj: any) => _.pick(obj, keys)),
      _.map(array2, (obj: any) => _.pick(obj, keys))
    );
  };
  hasText(text: string): boolean {
    return text.split(' ')?.length > 1 ? true : false;
  }

  getCustomFieldHtml(text): any {
    const openLink = this.translateService.instant('text.open_link');
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;
    if(text?.match(urlRegex)?.length > 0 && this.hasText(text)){
        return text.replace(urlRegex, (url: any)=> '<a target="_blank" href="' + url + '" class="hover:underline text-primary-500">' + url + '</a>');
    }
    else if(text?.match(urlRegex)?.length === 1){
        return text.replace(urlRegex,(url: any) =>'<a target="_blank" href="' + url + '" class="hover:underline text-primary-500">' + openLink + '</a>');
    }
    else if(!this.isURL(text) && !this.isEmptyOrUndefined(text)){
        return '<span>' +text +'</span>';
    }else{
      return '';
    }
  }

  updateNewTimeoff(data: any): any {
    this.updateNewTimeoffConfirmation.next(data);
  }

  getNewTimeoff(): any {
    return this.updateNewTimeoffConfirmation.asObservable();
  }

  isValidJson(json): any{
    try {
      const jObject = JSON.parse(json);
      // Handle non-exception-throwing cases:
      // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
      // but... JSON.parse(null) returns null, and typeof null === "object",
      // so we must check for that, too. Thankfully, null is falsey, so this suffices:
      if (jObject && typeof jObject === 'object') {
          return jObject;
      }
  }
  catch (e) { }

  return false;
  }

  collectRouteData(routeSnapshot: ActivatedRouteSnapshot, isParamNeeded: boolean = false): any {
    let routeData: any = {};

    // Collect data from the current route snapshot
    if (routeSnapshot && routeSnapshot.data) {
      routeData = { ...routeData, ...routeSnapshot.data, ...(isParamNeeded ? routeSnapshot.params : {}) };
    }
    // Recursively collect data from child routes
    if (routeSnapshot && routeSnapshot.children) {
      routeSnapshot.children.forEach((child: any) => {
        const childData = this.collectRouteData(child, isParamNeeded);
        routeData = { ...routeData, ...childData };
      });
    }
    return routeData;
  }

  showCancelAlert(formName: string, state?: string): void {
    const message = `<span>${this.translateService.instant('text.create.form.cancel.description')}</span><br/><span>${this.translateService.instant('text.unsaved.text')}</span>`;
    const headerTitle: string = this.translateService.instant('cancel.text');
    const cancelDialog: any = this.zuperDialogService.open({
      title: headerTitle,
      type: 'error',
      message: message,
      actions: {
        confirm: {
          show: true,
          label: 'Yes, Cancel',
          color: 'error',
          confirmDisplayKey: 'text.cancel_dialog.confirm_key',
        },
        cancel: {
          show: true,
          label: 'No',
          cancelDisplayKey: 'no',
        },
      },
    });
    cancelDialog.afterClosed().subscribe((result: any) => {
      if (result === true) {
        window.history.back();
      }
    });
  }

  AllowUserToViewAllContract(): boolean{
    const companyConfig = this._localStorageService.getLocalStorage('company_configuration_data');
    const allowFeToViewContract = companyConfig?.service_contract?.allow_fe_view_all_contracts;
    const allowTlToViewContract = companyConfig?.service_contract?.allow_tl_view_all_contracts;
    const user = this._localStorageService.getLocalStorage('user');
    const returnValue =  user?.role?.role_key === 'FIELD_EXECUTIVE' ? allowFeToViewContract : allowTlToViewContract;

    if(user?.role?.role_key === 'ADMIN'){
      return this.hasModuleAccess('SERVICE_CONTRACTS', false, 'from_sidebar');
    }else if(['FIELD_EXECUTIVE','TEAM_LEADER'].includes(user?.role?.role_key)) {
        return returnValue && this.hasModuleAccess('SERVICE_CONTRACTS', false, 'from_sidebar');
    }

    return true;
}
  allowUserToViewContract(contractUser: any): boolean{
    const companyConfig = this._localStorageService.getLocalStorage('company_configuration_data');
    const allowFeToViewContract = companyConfig?.service_contract?.allow_fe_view_all_contracts;
    const allowTlToViewContract = companyConfig?.service_contract?.allow_tl_view_all_contracts;
    const user = this._localStorageService.getLocalStorage('user');
    const returnValue =  user?.role?.role_key === 'FIELD_EXECUTIVE' ? allowFeToViewContract : allowTlToViewContract;
    if(user?.role?.role_key === 'ADMIN'){
      return this.hasModuleAccess('SERVICE_CONTRACTS', false, 'from_sidebar');
    }else if(['FIELD_EXECUTIVE','TEAM_LEADER'].includes(user?.role?.role_key)) {
        if(returnValue === false){
          return contractUser === user?.email && this.hasModuleAccess('SERVICE_CONTRACTS', false, 'from_sidebar');
        }else{
          return this.hasModuleAccess('SERVICE_CONTRACTS', false, 'from_sidebar');
        }
    }
  }

  public getNumberOfDaysInMonth(monthIndex): any{
    const month = dayjs().month(monthIndex);
    return dayjs(month).daysInMonth();
  }

  public getEmailFromObject(object: any): string {
      return !this.isEmptyOrUndefined(object?.customer_address?.email) ? object?.customer_address?.email :
             !this.isEmptyOrUndefined(object?.customer?.customer_email) ? object?.customer?.customer_email :
             !this.isEmptyOrUndefined(object?.organization?.organization_email) ? object?.organization?.organization_email :
             !this.isEmptyOrUndefined(object?.customer_billing_address?.email) ? object?.customer_billing_address?.email : '';
  }

  // region UI SDK

  checkDiffAndCastToSdk(currentPage: string, control: AbstractControl, controlName?: string, checkDiff = true): Subscription {
    return control.valueChanges.pipe(debounceTime(1000), pairwise()).subscribe(([prev, next]: any[]) => {
      if (moment.isMoment(next) || moment.isMoment(prev)) {
        this.castEventToSdk(`${currentPage}:${controlName}`, {
          oldValue: { [controlName]: moment.isMoment(prev) ? prev.toDate() : prev },
          newValue: { [controlName]: moment.isMoment(next) ? next.toDate() : next }
        });
      }
      else if (typeof next === 'object' && checkDiff) {
        Object.keys(this.objectDiff(prev, next)).forEach((key: string) => {
          this.castEventToSdk(`${currentPage}:${key}`, {
            oldValue: { [key]: prev[key] },
            newValue: { [key]: next[key] }
          });
        });
      } else {
        this.castEventToSdk(`${currentPage}:${controlName}`, {
          oldValue: { [controlName]: prev },
          newValue: { [controlName]: next }
        });
      }
    });
  }

  public objectDiff(base: any, object: any): any {
    const changes = (childObject: any, childBase: any): any =>
      _.transform(childObject, (result, value, key) => {
        if (!_.isEqual(value, childBase[key])) {
          result[key] = (_.isObject(value) && _.isObject(childBase[key])) ? changes(value, childBase[key]) : value;
        }
      });
    return changes(object, base);
  }

  public convertMomentFieldsToDate(object: any): any {
    const data = {};
    Object.keys(object).forEach((key: any) => {
      if (moment.isMoment(object[key])) {
        data[key] = object[key].toDate();
      } else if (typeof object[key] === 'object') {
        data[key] = this.convertMomentFieldsToDate(object[key]);
      } else {
        data[key] = object[key];
      }
    });
    return data;
  }

  public castEventToSdk(event: string, data: any): void {
    this._eventBus.cast('sdk:' + event, data);
  }

  public castModalEventToSdk(event: string, data: any): void {
    this._eventBus.cast('modal:' + event, data);
  }

  public listenForFormPatchEvent(page: string): any {
    return this._eventBus.on('patchForm:' + page + ':**');
  }

  public convertMomentToDate(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(this.convertMomentToDate);
    } else if (obj !== null && typeof obj === 'object') {
      if (moment.isMoment(obj)) {
        return obj.toDate(); // Convert Moment.js object to Date
      } else {
        const result = {};
        for (const key in obj) {
          if (obj.hasOwnProperty(key)) {
            result[key] = this.convertMomentToDate(obj[key]);
          }
        }
        return result;
      }
    }
    return obj; // Return value as is if it's neither an object nor an array
  }

  public formControlHasError(formArray: UntypedFormArray, formControlName: string): boolean {
    return formArray.controls.some((control) => {
      if (control instanceof UntypedFormGroup) {
          const formControl = control.get(formControlName);
          return formControl?.invalid;
      }
      return false;
    });
  }

  /**
   * For B2B businesses, show Customer as Contact
   *
   * @returns true (if _**B2B**_ flag enabled and has _**Organization**_ module access)
   */
  public isCompanyB2BEnabled(): boolean {
    const COMPANY_POLICY: any = this._localStorageService.getLocalStorage('company_policy');
    const enableB2B: boolean = COMPANY_POLICY?.enable_b2b ?? false;
    return enableB2B && this.hasModuleAccess('ORGANIZATION');
  }

  showProductPaymentAlert(): void {
    // Retrieve the flag indicating if the product payment dialog already has been closed
    const isPaymentDialogClosed = this._localStorageService.getLocalStorage('product_payment_closed') ?? false;

    // Destructure the popup banner data if it's available
    const { is_enabled, dismissable, message, title } = this.companyPolicy?.popup_banner || {};

    // If the subscription dialog is enabled and dismissable is enabled, open the dialog
    if (is_enabled && message && title && !isPaymentDialogClosed) {
      const dialogConfig = {
        data: this.companyPolicy.popup_banner,
        dismissible: dismissable,
      };

      this.zuperDialogService.openCustom(ProductSubscriptionAlertComponent, dialogConfig).afterClosed().subscribe(() => {
        this._localStorageService.setLocalStorage('product_payment_closed', true);
      });
    }
  }

  isProductTaxCustom(tax): boolean{
    const hasCustomTax = !this.isEmptyOrUndefined(tax?.tax_name) && !this.isEmptyOrUndefined(tax?.tax_rate) && tax?.tax_rate>=0;
    return hasCustomTax;
  }

  hasSelectedRecord(products): boolean{
    return products?.findIndex((data: any)=> data?.selected === true) !== -1;
  }

  getSelectedProductCount(products): number{
    return products?.filter((data: any)=> data?.selected === true && data?.line_item_type !== 'HEADER')?.length;
  }
  
  fetchUsersZuperConnectAgentInfo(userUUIDs: string[]): Observable<any>{
    const dcConnectURl = this._localStorageService.getLocalStorage('dc_connect_url');
    if(!dcConnectURl){
      return of({})
    }
    const url = `${dcConnectURl}/api/telephony/agent/users`;
    return this.dataCollectorService.postData(url, {
      "filters": {
          "user_uid": userUUIDs
      }
    }).pipe(map(response=> {
      const agentsInfo = {};
      response.data.forEach((user: any)=>{
        agentsInfo[user.user_uid] = user.agent.agent_uid
      })
      return agentsInfo;
    }))
  }

  setPrepopulatedDateValue(field): any{
      const fieldType = field.field_type;
      const rangeFilter = {
        'SINGLE': this.advanceFilterDateRange.filter((data: any)=> ['TODAY','TOMORROW','YESTERDAY','CUSTOM_DATE']?.includes(data?.rangeKey)),
        'BETWEEN': this.advanceFilterDateRange.filter((data: any)=> !['TODAY','TOMORROW','YESTERDAY','CUSTOM_DATE']?.includes(data?.rangeKey))
      }
      switch(fieldType){
          case 'DATE':
            if(!this.isEmptyOrUndefined(field.rangeKey) && !['CUSTOM_DATE','CUSTOM_RANGE'].includes(field.rangeKey)){
                if(field?.operator === 'BETWEEN'){
                    const value = this.advanceFilterDateRange.find((data: any)=>data.rangeKey === field?.rangeKey)?.value;
                    field.value = value?.length > 0 ? [dayjs(this.advanceFilterDateRange.find((data: any)=>data.rangeKey === field?.rangeKey)?.value[0]).format('YYYY-MM-DD'),dayjs(this.advanceFilterDateRange.find((data: any)=>data.rangeKey === field?.rangeKey)?.value[1]).format('YYYY-MM-DD')] : "";
                }else{
                    const value = this.advanceFilterDateRange.find((data: any)=>data.rangeKey === field?.rangeKey)?.value;
                    field.value = value?.length > 0 ? [dayjs(this.advanceFilterDateRange.find((data: any)=>data.rangeKey === field?.rangeKey)?.value[0]).format('YYYY-MM-DD')] : "";
                }
            }else if(this.isEmptyOrUndefined(field.rangeKey)){
              field.rangeKey = null
            } 
          return  field;
          default:
            return field;
      }
  }

  get advanceFilterDateRange(): any {
      return [{
          key: `${this.getLocaleValue('daterange.range.today.label', 'Today')}`,
          rangeKey: 'TODAY',
          value: [dayjs()]
      },
      {
          key: `${this.getLocaleValue('daterange.range.yesterday.label', 'Yesterday')}`,
          rangeKey: 'YESTERDAY',
          value: [dayjs().subtract(1, 'days')]
      },
      {
          key: `${this.getLocaleValue('text.tomorrow', 'Tomorrow')}`,
          rangeKey: 'TOMORROW',
          value: [dayjs().add(1, 'days')]
      },
      {
          key: `${this.getLocaleValue('daterange.range.seven.days.label', 'Last 7 Days')}`,
          rangeKey: 'LAST_7_DAYS',
          value: [dayjs().subtract(7, 'days'), dayjs().subtract(1, 'days')]
      },
      {
          key: `${this.getLocaleValue('text.next.seven.days', 'Next 7 Days')}`,
          rangeKey: 'NEXT_7_DAYS',
          value: [dayjs().add(1, 'days'), dayjs().add(7, 'days')]
      },
      {
          key: `${this.getLocaleValue('daterange.range.thirty.days.label', 'Last 30 Days')}`,
          rangeKey: 'LAST_30_DAYS',
          value: [dayjs().subtract(30, 'days'), dayjs().subtract(1, 'days')]
      },
      {
          key: `${this.getLocaleValue('daterange.range.thismonth.label', 'This Month')}`,
          rangeKey: 'THIS_MONTH',
          value: [dayjs().startOf('month'), dayjs().endOf('month')]
      },
      {
          key: `${this.getLocaleValue('daterange.range.lastmonth.label', 'Last Month')}`,
          rangeKey: 'LAST_MONTH',
          value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]
      },
      {
          key: `${this.getLocaleValue('daterange.range.nextmonth.label', 'Next Month')}`,
          rangeKey: 'NEXT_MONTH',
          value: [dayjs().add(1, 'month').startOf('month'), dayjs().add(1, 'month').endOf('month')]
      },
      {
          key: `${this.getLocaleValue('daterange.range.nextthirtydays.label', 'Next 30 Days')}`,
          rangeKey: 'NEXT_30_DAYS',
          value: [dayjs().add(1, 'days'), dayjs().add(30, 'days')]
      },
      {
          key: `${this.getLocaleValue('text.custom.date', 'Custom Date')}`,
          rangeKey: 'CUSTOM_DATE',
          value: ['CUSTOM_DATE']
      },
      {
          key: `${this.getLocaleValue('text.custom_range', 'Custom Range')}`,
          rangeKey: 'CUSTOM_RANGE',
          value: ['CUSTOM_RANGE']
      }];
  }

  copyLink(url: string): void {
    this.clipboard.copy(url);
    this.toast.success(this.translateService.instant('text.link.copied'));
  }

  public get getCurrencyDisplaySymbol(): string {
    if (this.companyCurrency === 'SGD') {
      return 'S$';
    }
    const locale = LANG_COUNTRY.data[this.companyCountry] || 'en-US';
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: this.companyCurrency,
      currencyDisplay: 'symbol'
    }).format(0);
  
    return formatted.replace(/[\d.,\s]/g, '').trim();
  }
  
}
